/**
 * The load factor analysis on the Data tab: which days and routes ran emptiest.
 *
 * Route instances in, one point per compared date out, each carrying its routes' load factors,
 * the routes left out of the figure and what those cost. Forecast reads the plan; actual takes
 * what was collected where a stop has it.
 */
import { eachDayOfInterval, format, getDay, startOfDay, startOfWeek } from "date-fns"

import { formatDayMonth, formatDayShort } from "@/lib/dateFormats"
import { isInternalStopType } from "@/lib/haulageUtils"
import { routeCapacity, toCapacityStop, toUkPallets } from "@/lib/loadFactor"
import {
  applyPlannedMileageMultiplier,
  calculateDistanceFromPlannedStops,
  calculateDurationMinutes,
  findVehicleType,
  priceRoute,
} from "@/lib/routeCostCalculation"
import {
  CollectionsConfig,
  DayOfWeek,
  Location,
  RouteCostBreakdown,
  RouteInstance,
  RoutePlanned,
  VehicleType,
} from "@/types/collections"

/** Indexed by `getDay`: 0 is Sunday. */
export const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]

/** "weekday" compares one weekday across weeks; "week" shows every day in the range. */
export type ViewMode = "weekday" | "week"

export interface RouteDetail {
  code: string
  id: string
  loadFactorForecast: number
  loadFactorActual: number
  capacity: number
  usedForecast: number
  usedActual: number
  isAdHoc?: boolean
  stops: Array<{
    name: string
    stopType: string
    loadingUnitsForecastUKPallets: number
    loadingUnitsActualUKPallets?: number
    sequence: number
  }>
}

export interface WeekdayDataPoint {
  date: string
  dateLabel: string
  loadFactorForecast: number
  loadFactorActual: number
  totalRoutes: number
  totalCapacity: number
  totalUsedForecast: number
  totalUsedActual: number
  routesIncluded: number
  routesExcluded: number
  includedRouteCodes: string[]
  excludedRouteCodes: string[]
  routeDetails: RouteDetail[]
  excludedRoutes: Array<{
    code: string
    id: string
    reason: string
  }>
  excludedRoutesTotalCost?: RouteCostBreakdown | null
}

export interface LowLoadFactorRoute {
  date: string
  dateLabel: string
  lowLoadRoutes: number
  totalWastedCapacity: number
  routeCodes: string[]
  avgLoadFactor: number
}

/**
 * The dates being compared, in order.
 *
 * In weekday mode, every occurrence of `selectedWeekday` (0 = Sunday) in the range; otherwise
 * every day in it.
 */
export function comparisonDatesFor(
  range: { from?: Date; to?: Date } | undefined,
  viewMode: ViewMode,
  selectedWeekday: number,
): Date[] {
  if (!range?.from || !range?.to) return []

  const start = startOfDay(range.from)
  const end = startOfDay(range.to)
  const dates: Date[] = []

  if (viewMode === "weekday") {
    let currentDate = new Date(start)
    const endDate = new Date(end)

    while (currentDate <= endDate) {
      const dayOfWeek = getDay(currentDate)
      if (dayOfWeek === selectedWeekday) {
        dates.push(new Date(currentDate))
      }
      currentDate = new Date(currentDate)
      currentDate.setDate(currentDate.getDate() + 1)
    }
  } else {
    const allDays = eachDayOfInterval({ start, end })

    const weekGroups = new Map<string, Date[]>()
    allDays.forEach((day) => {
      const weekStart = startOfWeek(day, { weekStartsOn: 1 })
      const weekKey = format(weekStart, "yyyy-MM-dd")
      if (!weekGroups.has(weekKey)) {
        weekGroups.set(weekKey, [])
      }
      weekGroups.get(weekKey)!.push(day)
    })

    // Every day in range, whole weeks or not.
    weekGroups.forEach((days) => {
      dates.push(...days)
    })

    dates.sort((a, b) => a.getTime() - b.getTime())
  }

  return dates
}

/** Sum the excluded routes' costs into one breakdown, or null when there are none. */
function sumExcludedRouteCosts(
  excludedRouteCosts: RouteCostBreakdown[],
): RouteCostBreakdown | null {
  if (excludedRouteCosts.length === 0) return null
  const totalVehicleLease = excludedRouteCosts.reduce(
    (sum, cost) => sum + (cost.vehicle_lease_cost || 0),
    0,
  )
  const totalCharging = excludedRouteCosts.reduce((sum, cost) => sum + (cost.charging_cost || 0), 0)
  const totalDriverWage = excludedRouteCosts.reduce(
    (sum, cost) => sum + (cost.driver_wage_cost || 0),
    0,
  )
  const totalCost = totalVehicleLease + totalCharging + totalDriverWage
  const totalForecastVolume = excludedRouteCosts.reduce(
    (sum, cost) => sum + (cost.total_forecast_volume || 0),
    0,
  )
  const costPerParcel = totalForecastVolume > 0 ? totalCost / totalForecastVolume : undefined

  // Summed like every other component rather than defaulted to 0, so an
  // excluded haulage route's revenue is not silently dropped.
  const totalHaulageRevenue = excludedRouteCosts.reduce(
    (sum, cost) => sum + (cost.total_haulage_delivery_revenue || 0),
    0,
  )

  return {
    total_haulage_delivery_revenue: totalHaulageRevenue,
    vehicle_lease_cost: totalVehicleLease,
    charging_cost: totalCharging,
    driver_wage_cost: totalDriverWage,
    total_cost: totalCost,
    cost_per_parcel: costPerParcel,
    total_forecast_volume: totalForecastVolume > 0 ? totalForecastVolume : undefined,
  }
}

interface BuildWeekdayDataParams {
  routeInstances: RouteInstance[]
  comparisonDates: Date[]
  locations: Location[] | undefined
  vehicleTypes: VehicleType[] | undefined
  config: CollectionsConfig | undefined
  viewMode: ViewMode
}

/**
 * One point per compared date: the day's load factor across the routes that collect from a
 * shipper, with each route's own figure and the routes left out.
 *
 * A route with no shipper collection is excluded and priced instead. A route whose vehicle type
 * is unknown, or which has no non-depot stops, or whose vehicle has no UK pallet capacity, is
 * counted as included but contributes nothing to the figure.
 */
export function buildWeekdayData({
  routeInstances,
  comparisonDates,
  locations,
  vehicleTypes,
  config,
  viewMode,
}: BuildWeekdayDataParams): WeekdayDataPoint[] {
  if (!routeInstances.length) return []
  if (!locations || !vehicleTypes) return []

  const convertToUKPallets = (loadingUnits: number, unitType?: string): number =>
    toUkPallets(loadingUnits, unitType, config)

  const instancesByDate = new Map<string, RouteInstance[]>()

  routeInstances.forEach((instance: RouteInstance) => {
    const dateStr =
      typeof instance.date === "string"
        ? instance.date.split("T")[0]
        : format(new Date(instance.date), "yyyy-MM-dd")

    if (!instancesByDate.has(dateStr)) {
      instancesByDate.set(dateStr, [])
    }
    instancesByDate.get(dateStr)!.push(instance)
  })

  const dataPoints: WeekdayDataPoint[] = []

  comparisonDates.forEach((date) => {
    const dateStr = format(date, "yyyy-MM-dd")
    const instances = instancesByDate.get(dateStr) || []

    let totalCapacity = 0
    let totalUsedForecast = 0
    let totalUsedActual = 0
    const includedRouteCodes: string[] = []
    const excludedRouteCodes: string[] = []
    const routeDetails: RouteDetail[] = []
    const excludedRoutes: Array<{ code: string; id: string; reason: string }> = []
    const excludedRouteCosts: RouteCostBreakdown[] = []

    instances.forEach((instance) => {
      const allStops = instance.stops || []
      const routeCode = instance.code || ""

      const hasShipperCollection = allStops.some((stop) => {
        const stopType = stop.stop_type
        return stopType === "shipper_collection"
      })

      if (!hasShipperCollection) {
        if (routeCode) {
          excludedRouteCodes.push(routeCode)

          const plannedRoute = instance.planned_route
          const routeCost = config
            ? priceRoute({
                vehicleTypes,
                vehicleTypeRef: plannedRoute?.vehicle_type || instance.vehicle_type,
                config,
                distanceMiles: instance.distance_miles,
                durationMinutes: instance.duration_minutes,
                day: {
                  stops: allStops,
                  crewSize: instance.crew_size,
                  routeKind: instance.route_kind,
                },
                plan: { crewSize: plannedRoute?.crew_size, routeKind: plannedRoute?.route_kind },
              })
            : null
          if (routeCost) excludedRouteCosts.push(routeCost)

          excludedRoutes.push({
            code: routeCode,
            id: instance.id,
            reason: "No shipper_collection stops",
          })
        }
        return
      }

      if (routeCode) includedRouteCodes.push(routeCode)

      const plannedRoute = instance.planned_route
      const isAdHoc = instance.is_adhoc === true
      const vehicleTypeName = plannedRoute?.vehicle_type || instance.vehicle_type

      // findVehicleType rather than a lookup by name, to match the Monitoring tab: it checks both
      // name and display_name.
      let vehicleType = vehicleTypeName ? findVehicleType(vehicleTypes, vehicleTypeName) : null

      // Ad-hoc routes often have no planned_route (so no vehicle_type); use the first vehicle type
      // as a fallback so they appear in the Daily Breakdown.
      if (!vehicleType && isAdHoc && vehicleTypes.length > 0) {
        vehicleType = vehicleTypes[0]
      }

      if (!vehicleType) return

      // Filter stops: exclude depot/internal stops (matching Monitoring tab logic)
      const stopInstances = allStops.filter((stop) => {
        const stopType = stop.stop_type
        return !isInternalStopType(stopType)
      })

      if (stopInstances.length === 0) return

      // Forecast reads the plan; actual takes what was collected where a stop has it.
      const capacityParams = { vehicleType, locations, config }
      const forecastCapacity = routeCapacity({
        ...capacityParams,
        stops: stopInstances.map((stop) => toCapacityStop(stop)),
      })
      const actualCapacity = routeCapacity({
        ...capacityParams,
        stops: stopInstances.map((stop) => toCapacityStop(stop, { preferActuals: true })),
      })

      if (forecastCapacity && actualCapacity) {
        // UK pallet capacity straight from the vehicle type, to match the Monitoring tab
        const capacity = vehicleType.uk_pallet_capacity || 0
        const usedForecastUKPallets = forecastCapacity.ukPalletsUsed
        const usedActualUKPallets = actualCapacity.ukPalletsUsed

        if (capacity > 0) {
          const routeLoadFactorForecast = (usedForecastUKPallets / capacity) * 100
          const routeLoadFactorActual = (usedActualUKPallets / capacity) * 100
          totalCapacity += capacity
          totalUsedForecast += usedForecastUKPallets
          totalUsedActual += usedActualUKPallets

          // Shipper collection stops only, in UK pallets
          const routeStops = stopInstances
            .filter((stop) => {
              const stopType = stop.stop_type
              return stopType === "shipper_collection"
            })
            .map((stop) => {
              const unitType = stop.loading_unit_type
              const forecastUnits = stop.loading_units_forecast || 0
              const actualUnits = stop.loading_units_actual

              return {
                name: stop.name || "",
                stopType: stop.stop_type || "",
                loadingUnitsForecastUKPallets: convertToUKPallets(forecastUnits, unitType),
                loadingUnitsActualUKPallets:
                  actualUnits !== undefined && actualUnits !== null && actualUnits > 0
                    ? convertToUKPallets(actualUnits, unitType)
                    : undefined,
                sequence: stop.sequence || 0,
              }
            })
            .sort((a, b) => a.sequence - b.sequence)

          routeDetails.push({
            code: routeCode,
            id: instance.id,
            loadFactorForecast: routeLoadFactorForecast,
            loadFactorActual: routeLoadFactorActual,
            capacity,
            usedForecast: usedForecastUKPallets,
            usedActual: usedActualUKPallets,
            isAdHoc,
            stops: routeStops,
          })
        }
      }
    })

    const loadFactorForecast = totalCapacity > 0 ? (totalUsedForecast / totalCapacity) * 100 : 0
    const loadFactorActual = totalCapacity > 0 ? (totalUsedActual / totalCapacity) * 100 : 0
    const dateLabel = viewMode === "week" ? formatDayShort(date) : formatDayMonth(date)

    dataPoints.push({
      date: dateStr,
      dateLabel,
      loadFactorForecast: Math.round(loadFactorForecast * 10) / 10,
      loadFactorActual: Math.round(loadFactorActual * 10) / 10,
      totalRoutes: instances.length,
      totalCapacity: Math.round(totalCapacity * 10) / 10,
      totalUsedForecast: Math.round(totalUsedForecast * 10) / 10,
      totalUsedActual: Math.round(totalUsedActual * 10) / 10,
      routesIncluded: includedRouteCodes.length,
      routesExcluded: excludedRouteCodes.length,
      includedRouteCodes: includedRouteCodes.sort(),
      excludedRouteCodes: excludedRouteCodes.sort(),
      routeDetails: routeDetails.sort((a, b) => a.loadFactorForecast - b.loadFactorForecast),
      excludedRoutes: excludedRoutes.sort((a, b) => a.code.localeCompare(b.code)),
      excludedRoutesTotalCost: sumExcludedRouteCosts(excludedRouteCosts),
    })
  })

  return dataPoints
}

/** Actual where a day or route has one, forecast otherwise. */
const effectiveLoadFactor = (d: { loadFactorActual: number; loadFactorForecast: number }) =>
  d.loadFactorActual > 0 ? d.loadFactorActual : d.loadFactorForecast

export interface OverallMetrics {
  avgLoadFactor: number
  totalRoutes: number
  /** UK pallets of capacity left empty across the period. */
  totalWastedCapacity: number
  /** Least-squares slope of the daily load factor, in points per data point. */
  trend: number
  trendDirection: "increasing" | "decreasing" | "stable"
}

/** The headline figures over the period, actual where available. Null when there is no data. */
export function overallMetricsFor(weekdayData: WeekdayDataPoint[]): OverallMetrics | null {
  if (weekdayData.length === 0) return null

  const loadFactors = weekdayData.map(effectiveLoadFactor)
  const avgLoadFactor = loadFactors.reduce((sum, lf) => sum + lf, 0) / loadFactors.length
  const totalRoutes = weekdayData.reduce((sum, d) => sum + d.routesIncluded, 0)

  const totalWastedCapacity = weekdayData.reduce((sum, d) => {
    const used = d.totalUsedActual > 0 ? d.totalUsedActual : d.totalUsedForecast
    return sum + (d.totalCapacity - used)
  }, 0)

  const n = weekdayData.length
  const xValues = weekdayData.map((_, i) => i)
  const yValues = loadFactors
  const xMean = xValues.reduce((a, b) => a + b, 0) / n
  const yMean = yValues.reduce((a, b) => a + b, 0) / n
  let numerator = 0
  let denominator = 0
  for (let i = 0; i < n; i++) {
    numerator += (xValues[i] - xMean) * (yValues[i] - yMean)
    denominator += Math.pow(xValues[i] - xMean, 2)
  }
  const trend = denominator === 0 ? 0 : numerator / denominator

  return {
    avgLoadFactor,
    totalRoutes,
    totalWastedCapacity,
    trend,
    trendDirection: trend > 0.1 ? "increasing" : trend < -0.1 ? "decreasing" : "stable",
  }
}

/** The five emptiest days under 60% that ran at least one included route, emptiest first. */
export function inefficientDaysIn(
  weekdayData: WeekdayDataPoint[],
): (WeekdayDataPoint & { loadFactor: number })[] {
  return weekdayData
    .map((d) => ({ ...d, loadFactor: effectiveLoadFactor(d) }))
    .filter((d) => d.loadFactor < 60 && d.routesIncluded > 0)
    .sort((a, b) => a.loadFactor - b.loadFactor)
    .slice(0, 5)
}

/**
 * Days with two or more routes under 50% — candidates for consolidation — the five with the most
 * wasted capacity first.
 */
export function lowLoadFactorRoutesIn(weekdayData: WeekdayDataPoint[]): LowLoadFactorRoute[] {
  const opportunities: LowLoadFactorRoute[] = []

  weekdayData.forEach((point) => {
    const lowLoadRoutes = point.routeDetails.filter((r) => effectiveLoadFactor(r) < 50)

    if (lowLoadRoutes.length >= 2) {
      const totalWasted = lowLoadRoutes.reduce((sum, r) => {
        const used = r.usedActual > 0 ? r.usedActual : r.usedForecast
        return sum + (r.capacity - used)
      }, 0)
      const avgLoad =
        lowLoadRoutes.reduce((sum, r) => sum + effectiveLoadFactor(r), 0) / lowLoadRoutes.length

      opportunities.push({
        date: point.date,
        dateLabel: point.dateLabel,
        lowLoadRoutes: lowLoadRoutes.length,
        totalWastedCapacity: totalWasted,
        routeCodes: lowLoadRoutes.map((r) => r.code),
        avgLoadFactor: avgLoad,
      })
    }
  })

  return opportunities.sort((a, b) => b.totalWastedCapacity - a.totalWastedCapacity).slice(0, 5)
}

/** Vehicle range in miles from a vehicle type name ("HGV-Curtainsider-350mi" -> "350"). */
export function parseVehicleRangeMiles(vehicleTypeName: string | undefined): string {
  if (!vehicleTypeName) return ""
  const match = vehicleTypeName.match(/(\d+)\s*mi/i)
  return match ? match[1] : ""
}

export const PLANNED_ROUTES_CSV_HEADER = [
  "Day",
  "Route Code",
  "Shippers",
  "Vehicle Planned",
  "Vehicle Range (miles)",
  "Total Mileage",
  "Planned Start",
  "Planned End",
  "Duration (min)",
]

/**
 * One CSV row per planned route per day it runs, Monday first, then by route code.
 *
 * Mileage is stop 1 → 2 → 3… by Haversine with the global mileage % applied. Cancelled routes and
 * routes with no stops are left out.
 */
export function plannedRoutesCsvRows(
  plannedRoutes: RoutePlanned[],
  config: CollectionsConfig | undefined,
): string[][] {
  const rows: string[][] = []

  const dayOrder: Record<string, number> = {
    Monday: 0,
    Tuesday: 1,
    Wednesday: 2,
    Thursday: 3,
    Friday: 4,
    Saturday: 5,
    Sunday: 6,
  }

  plannedRoutes.forEach((route: RoutePlanned) => {
    if (route.status === "cancelled") return

    const stops = (route.stops ?? [])
      .slice()
      .sort((a: { sequence: number }, b: { sequence: number }) => a.sequence - b.sequence)
    if (stops.length === 0) return

    const shipperStops = stops.filter(
      (s) => s.stop_type === "shipper_collection",
    )
    const shipperNames =
      shipperStops.length > 0
        ? [
            ...new Set(
              shipperStops
                .map((s) => {
                  const name = s.location?.site_name || ""
                  const postcode = s.location?.postcode || ""
                  return name ? (postcode ? `${name} (${postcode})` : name) : ""
                })
                .filter(Boolean),
            ),
          ].join("; ")
        : ""
    const vehicleTypeName = route.vehicle_type ?? ""
    const vehicleRange = parseVehicleRangeMiles(vehicleTypeName)

    const stopsWithCoords = stops
      .filter(
        (s) => s.location && s.location.latitude != null && s.location.longitude != null,
      )
      .map((s) => ({ lat: s.location.latitude, lng: s.location.longitude }))
    const rawMileage = calculateDistanceFromPlannedStops(stopsWithCoords)
    const totalMileage = applyPlannedMileageMultiplier(rawMileage, config) ?? rawMileage

    const startTime = route.default_start_time
      ? new Date(`1970-01-01T${route.default_start_time}`)
      : null
    let endTime = route.default_end_time ? new Date(`1970-01-01T${route.default_end_time}`) : null
    if (startTime && endTime && endTime <= startTime) {
      endTime = new Date(`1970-01-02T${route.default_end_time}`)
    }
    const durationMinutes =
      startTime && endTime ? calculateDurationMinutes(startTime, endTime) : null

    const plannedStart = route.default_start_time ?? ""
    const plannedEnd = route.default_end_time ?? ""
    const durationStr = durationMinutes != null ? String(durationMinutes) : ""

    const days = route.days ?? []
    days.forEach((day: DayOfWeek) => {
      const dayName = day.charAt(0).toUpperCase() + day.slice(1) // monday -> Monday
      rows.push([
        dayName,
        route.code || "",
        shipperNames,
        vehicleTypeName,
        vehicleRange,
        totalMileage != null ? String(totalMileage) : "",
        plannedStart,
        plannedEnd,
        durationStr,
      ])
    })
  })

  rows.sort((a, b) => {
    const dayDiff = (dayOrder[a[0]] ?? 7) - (dayOrder[b[0]] ?? 7)
    return dayDiff !== 0 ? dayDiff : a[1].localeCompare(b[1])
  })

  return rows
}
